import {
  ADMIN_BULK_ANALYSIS_COMPS_API,
  ADMIN_IMAGE_ANALYSIS_API,
  BulkAnalysisComp,
  REQUEST_ID_HEADER,
  UADAnalysisResult,
  randomUUID,
} from '@aloft/toolkit-shared';
import { Block, Button, Flex, Typography } from '@aloft/toolkit-sui';
import { useAuth0 } from '@auth0/auth0-react';

import { useCallback, useState } from 'react';

import { LoadingScreenSamFull } from '../../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import { getBaseUrl } from '../../../hooks/api/getBaseUrl.ts';
import { BulkAnalysisForm, BulkAnalysisFormSubmit } from './components/BulkAnalysisForm.tsx';
import { BulkCompDetailModal } from './components/BulkCompDetailModal.tsx';
import { BulkCompResult, parseRatingLabel } from './components/bulkAnalysisTypes.ts';
import { AnalysisMode } from './components/imageAnalysisFormConfig.ts';
import { BulkResultsSummary } from './components/BulkResultsSummary.tsx';
import { BulkResultsTable } from './components/BulkResultsTable.tsx';
import { BulkRunPills } from './components/BulkRunPills.tsx';

const MAX_CONCURRENCY = 20;

/** Simple counting semaphore. Resolves when a slot is available. */
const createSemaphore = (limit: number) => {
  let active = 0;
  const queue: (() => void)[] = [];

  const acquire = () =>
    new Promise<void>((resolve) => {
      if (active < limit) {
        active++;
        resolve();
      } else {
        queue.push(() => {
          active++;
          resolve();
        });
      }
    });

  const release = () => {
    active--;
    const next = queue.shift();
    if (next) next();
  };

  return { acquire, release };
};

type BulkState =
  | { status: 'idle' }
  | { status: 'fetchingComps' }
  | { status: 'running'; results: BulkCompResult[]; total: number }
  | { status: 'complete'; results: BulkCompResult[]; total: number }
  | { status: 'error'; message: string };

export const BulkAnalysisContent = ({ analysisMode }: { analysisMode: AnalysisMode }) => {
  const [state, setState] = useState<BulkState>({ status: 'idle' });
  const [selectedResult, setSelectedResult] = useState<BulkCompResult | null>(null);
  const [usedForm, setUsedForm] = useState<BulkAnalysisFormSubmit | null>(null);
  const { getAccessTokenSilently } = useAuth0();

  const authFetch = useCallback(
    async (url: string, body: object) => {
      const token = await getAccessTokenSilently();
      return window.fetch(`${getBaseUrl()}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          [REQUEST_ID_HEADER]: randomUUID(),
        },
        body: JSON.stringify(body),
      });
    },
    [getAccessTokenSilently],
  );

  const runAnalysisForComp = useCallback(
    async (
      comp: BulkAnalysisComp,
      formValues: BulkAnalysisFormSubmit,
    ): Promise<Omit<BulkCompResult, 'comp'>> => {
      const startMs = Date.now();
      const common = {
        mode: formValues.analysisMode,
        model: formValues.model,
        promptOverride: formValues.promptOverride,
        sample: formValues.sample,
        compress: formValues.compress,
      };

      try {
        const response = await authFetch('/api/v2/admin/imageAnalysis', {
          ...common,
          lookupType: 'imageURLs',
          imageURLs: comp.imageURLs,
        });

        const text = await response.text();
        const trimmed = text.trimStart();

        let parsed: unknown;
        try {
          parsed = JSON.parse(trimmed);
        } catch {
          return { status: 'error', errorMessage: 'Failed to parse response' };
        }

        if (!response.ok || (parsed as any)?.error != null) {
          return {
            status: 'error',
            errorMessage: (parsed as any)?.error ?? `Server error ${response.status}`,
          };
        }

        const result = ADMIN_IMAGE_ANALYSIS_API.POST.RESPONSE.parse(parsed);
        const timingMs = Date.now() - startMs;

        if (result.mode === 'uadRatings') {
          const content = (result as UADAnalysisResult).content;
          return {
            status: 'success',
            aiQuality: parseRatingLabel(content.uad_quality_rating),
            aiCondition: parseRatingLabel(content.uad_condition_rating),
            timingMs,
            fullResult: result,
          };
        }

        // craftsmanQuality — no condition rating
        const craftsmanContent = result.content as any;
        return {
          status: 'success',
          aiQuality: typeof craftsmanContent?.overall_class === 'number'
            ? Math.round(craftsmanContent.overall_class)
            : undefined,
          timingMs,
          fullResult: result,
        };
      } catch (err) {
        return {
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
        };
      }
    },
    [authFetch],
  );

  const handleSubmit = useCallback(
    async (formValues: BulkAnalysisFormSubmit) => {
      setState({ status: 'fetchingComps' });
      setUsedForm(formValues);

      // Phase 1: fetch comp list
      let comps: BulkAnalysisComp[];
      try {
        const response = await authFetch('/api/v2/admin/bulkAnalysisComps', {
          count: formValues.count,
          ...(formValues.qualityFilter != null ? { qualityFilter: formValues.qualityFilter } : {}),
          ...(formValues.conditionFilter != null ? { conditionFilter: formValues.conditionFilter } : {}),
          ...(formValues.stateFilter != null ? { stateFilter: formValues.stateFilter } : {}),
          ...(formValues.zipFilter != null ? { zipFilter: formValues.zipFilter } : {}),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          setState({ status: 'error', message: (err as any)?.error ?? `Server error ${response.status}` });
          return;
        }
        const data = ADMIN_BULK_ANALYSIS_COMPS_API.POST.RESPONSE.parse(await response.json());
        comps = data.comps;
      } catch (err) {
        setState({ status: 'error', message: err instanceof Error ? err.message : 'Unknown error' });
        return;
      }

      if (comps.length === 0) {
        setState({ status: 'error', message: 'No eligible comps found in completed orders.' });
        return;
      }

      // Phase 2: run analysis with bounded concurrency
      const initialResults: BulkCompResult[] = comps.map((comp) => ({
        comp,
        status: 'pending',
      }));

      setState({ status: 'running', results: initialResults, total: comps.length });

      const semaphore = createSemaphore(MAX_CONCURRENCY);

      await Promise.all(
        comps.map(async (comp, idx) => {
          await semaphore.acquire();
          try {
            const outcome = await runAnalysisForComp(comp, formValues);
            setState((prev) => {
              if (prev.status !== 'running') return prev;
              const next = [...prev.results];
              next[idx] = { comp, ...outcome };
              const allDone = next.every((r) => r.status !== 'pending');
              return allDone
                ? { status: 'complete', results: next, total: prev.total }
                : { ...prev, results: next };
            });
          } finally {
            semaphore.release();
          }
        }),
      );
    },
    [authFetch, runAnalysisForComp],
  );

  const results = state.status === 'running' || state.status === 'complete' ? state.results : [];
  const total = state.status === 'running' || state.status === 'complete' ? state.total : 0;
  const isPending = state.status === 'fetchingComps' || state.status === 'running';
  const showResults = state.status === 'running' || state.status === 'complete';

  return (
    <>
      <div style={{ display: showResults || isPending ? 'none' : undefined }}>
        <BulkAnalysisForm onSubmit={(v) => { void handleSubmit(v); }} isSubmitting={isPending} analysisMode={analysisMode} />
      </div>

      {state.status === 'fetchingComps' && (
        <Block position="relative" minHeight="400px">
          <LoadingScreenSamFull
            isLoading
            what="Fetching comps"
            wrapperProps={{ position: 'absolute', zIndex: '10' }}
          />
        </Block>
      )}

      {state.status === 'error' && (
        <Flex flexDirection="column" gap="16px">
          <Block padding="12px" borderRadius="4px" backgroundColor="alertErrorLight">
            <Typography variant="body1">{state.message}</Typography>
          </Block>
          <Flex>
            <Button text="Try Again" onClick={() => setState({ status: 'idle' })} size="sm" />
          </Flex>
        </Flex>
      )}

      {showResults && (
        <Flex flexDirection="column" gap="24px">
          <Flex justifyContent="space-between" alignItems="center">
            <Typography variant="h2">Bulk Analysis</Typography>
            {state.status === 'complete' && (
              <Button text="New Analysis" onClick={() => setState({ status: 'idle' })} size="sm" />
            )}
          </Flex>

          {usedForm != null && (
            <BulkRunPills formValues={usedForm} compsCount={total} />
          )}

          <BulkResultsSummary
            results={results}
            total={total}
            isRunning={state.status === 'running'}
          />

          <BulkResultsTable results={results} onSelect={setSelectedResult} />
        </Flex>
      )}

      <BulkCompDetailModal result={selectedResult} onClose={() => setSelectedResult(null)} />
    </>
  );
};
